import {
  ExternalPlatformIcon,
  getExternalPlatformLabel,
  Text,
} from '@shared/components';
import {
  InteractiveCard,
  QuickReplyButtons,
} from '@shared/features/interactive-card';
import {
  ChatMessageEvent,
  ConversationContributionRole,
} from '@shared/types/conversation';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { isQuickReplyCard } from 'clerk_common/cards/quickReply';
import {
  parseToSequence,
  TextOrMentionElement,
} from 'clerk_common/collaboration/mentions/parse';
import clsx from 'clsx';
import { Fragment } from 'react';
import { ChatAttachments } from './ChatAttachments';
import { roleToClassName } from './Transcript';

const ROOT = makeRootClassName('ConversationTranscript');
const el = makeElementClassNameFactory(ROOT);

type ChatContributionProps = {
  contribution: ChatMessageEvent;
  suppressSystemMessages?: boolean;
  aiSide?: 'left' | 'right';
};
export const ChatContribution = ({
  contribution,
  suppressSystemMessages,
  aiSide,
}: ChatContributionProps) => {
  const sequence = parseToSequence(contribution.message);

  const roleClassName = roleToClassName(contribution.role);
  const shouldSuppressSystemMessages =
    suppressSystemMessages &&
    contribution.role === ConversationContributionRole.SYSTEM;
  const className = clsx(el`chat-contribution`, {
    [roleClassName]: true,
  });
  const assistantClassName =
    aiSide === 'right' ? 'ASSISTANT-RIGHT' : 'ASSISTANT-LEFT';
  const wrapperClassName = clsx(el`chat-contribution-wrapper`, {
    [roleClassName]: true,
    [assistantClassName]: true,
  });

  if (shouldSuppressSystemMessages) return null;

  if (contribution.card && !isQuickReplyCard(contribution.card)) {
    return (
      <div className={wrapperClassName}>
        <InteractiveCard card={contribution.card} />
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <div className={className}>
        <span className="whitespace-pre-wrap">
          <Text type="body-xs">
            {sequence.map((e: TextOrMentionElement, idx: number) => (
              <Fragment key={idx}>
                {e.elementType === 'mention' ? (
                  <strong>@{e.display || e.id}</strong>
                ) : (
                  e.text
                )}
              </Fragment>
            ))}
          </Text>
        </span>
      </div>
      {contribution.card && (
        <QuickReplyButtons
          card={contribution.card}
          conversationId={contribution.conversationId}
        />
      )}
      {contribution.attachments && contribution.attachments.length > 0 && (
        <ChatAttachments attachments={contribution.attachments} />
      )}
      {contribution.externalPlatform && (
        <div className="flex items-center gap-1">
          <span aria-hidden>
            <ExternalPlatformIcon
              platform={contribution.externalPlatform}
              size={12}
            />
          </span>
          <Text type="body-xs" className="text-gray-400">
            via {getExternalPlatformLabel(contribution.externalPlatform)}
          </Text>
        </div>
      )}
    </div>
  );
};
